import { useState, useCallback, useRef, useEffect } from 'react';
import type { BatchUpgradeRequest, BatchUpgradeProgress } from '../types';
import {
  startBatchUpgrade,
  fetchBatchProgress,
  cancelBatchUpgrade,
} from '../api/containerApi';

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

export interface UseBatchUpgradeReturn {
  initiateBatch: (request: BatchUpgradeRequest) => Promise<string>;
  progress: BatchUpgradeProgress | null;
  cancelBatch: () => Promise<void>;
  isRunning: boolean;
  currentBatchId: string | null;
  error: string | null;
  clearError: () => void;
}

const POLL_INTERVAL_MS = 3000;

export const useBatchUpgrade = (authFetch: AuthFetch): UseBatchUpgradeReturn => {
  const [currentBatchId, setCurrentBatchId] = useState<string | null>(null);
  const [progress, setProgress] = useState<BatchUpgradeProgress | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (batchId: string) => {
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const data = await fetchBatchProgress(authFetch, batchId);

          // If a tenant is actively upgrading, fetch its per-tenant step progress
          if (data.current_tenant && data.in_progress > 0) {
            try {
              const detail = data.details?.find(
                (d: { tenant_slug?: string; tenant_id?: string }) =>
                  d.tenant_slug === data.current_tenant || d.tenant_id === data.current_tenant
              );
              if (detail?.tenant_id) {
                const statusResp = await authFetch(
                  `/api/admin/upgrades/tenants/${detail.tenant_id}/status`
                );
                if (statusResp.ok) {
                  const status = await statusResp.json();
                  if (status) {
                    data.current_step = status.current_step;
                    data.current_step_pct = status.progress_pct;
                  }
                }
              }
            } catch {
              // Per-tenant status is best-effort
            }
          }

          setProgress(data);
          const done = data.completed + data.failed + (data.skipped || 0) >= data.total;
          if (done) {
            setIsRunning(false);
            stopPolling();
          }
        } catch {
          // Polling errors are transient; keep trying
        }
      }, POLL_INTERVAL_MS);
    },
    [authFetch, stopPolling]
  );

  const initiateBatch = useCallback(
    async (request: BatchUpgradeRequest): Promise<string> => {
      setError(null);
      try {
        const batchId = await startBatchUpgrade(authFetch, request);
        setCurrentBatchId(batchId);
        setIsRunning(true);
        setProgress({ total: request.tenant_ids.length, completed: 0, failed: 0, skipped: 0, in_progress: 0, errors: [] });
        startPolling(batchId);
        return batchId;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to start batch upgrade');
        throw err;
      }
    },
    [authFetch, startPolling]
  );

  const cancelBatch = useCallback(async () => {
    if (!currentBatchId) return;
    setError(null);
    try {
      await cancelBatchUpgrade(authFetch, currentBatchId);
      stopPolling();
      setIsRunning(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel batch');
    }
  }, [authFetch, currentBatchId, stopPolling]);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  return { initiateBatch, progress, cancelBatch, isRunning, currentBatchId, error, clearError };
};
